/**
 * Unsent form values, kept per account and organization so that signing in again, a provider redirect or a
 * reload does not discard what a person typed. Forms that carry secrets must not use it.
 */
export class Drafts {
	static readonly #prefix = 'beyond.cdn.draft';
	#storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined;
	#owner = '';

	constructor(storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>) {
		this.#storage = storage;
	}

	/**
	 * Drafts belong to one account in one organization; another owner never reads them
	 */
	own(user: string | undefined, organization: string | undefined) {
		this.#owner = user ? `${user}:${organization ?? ''}` : '';
	}

	#key(form: string) {
		return `${Drafts.#prefix}:${this.#owner}:${form}`;
	}

	read<Value>(form: string): Value | undefined {
		if (!this.#owner) return undefined;
		try {
			const kept = this.#storage?.getItem(this.#key(form));
			return kept ? (JSON.parse(kept) as Value) : undefined;
		} catch {
			return undefined;
		}
	}

	write(form: string, value: unknown) {
		if (!this.#owner) return;
		try {
			this.#storage?.setItem(this.#key(form), JSON.stringify(value));
		} catch {
			// The draft stays in memory only
		}
	}

	/** Someone else signed in: every draft kept on this tab is dropped, whoever it belonged to */
	drop() {
		try {
			const storage = this.#storage as (Pick<Storage, 'removeItem'> & { length?: number; key?: (index: number) => string | null }) | undefined;
			const keys = Array.from({ length: storage?.length ?? 0 }, (_, index) => storage?.key?.(index)).filter(key => key?.startsWith(`${Drafts.#prefix}:`));
			for (const key of keys) storage?.removeItem(key as string);
		} catch {
			// Nothing to drop
		}
	}

	clear(form: string) {
		try {
			this.#storage?.removeItem(this.#key(form));
		} catch {
			// Nothing to clear
		}
	}
}
